import type { ComponentType, SVGProps } from 'react';
import { useSelector } from 'react-redux';
import ControllerChargingLevel1Icon from '../../Components/Icons/Power/controller/controller_level1_charging.svg?react';
import ControllerChargingLevel1IconDark from '../../Components/Icons/Power/controller/controller_level1_charging_dark.svg?react';
import ControllerChargingLevel2Icon from '../../Components/Icons/Power/controller/controller_level2_charging.svg?react';
import ControllerChargingLevel2IconDark from '../../Components/Icons/Power/controller/controller_level2_charging_dark.svg?react';
import ControllerChargingLevel3Icon from '../../Components/Icons/Power/controller/controller_level3_charging.svg?react';
import ControllerChargingLevel3IconDark from '../../Components/Icons/Power/controller/controller_level3_charging_dark.svg?react';
import ControllerChargingLevel4Icon from '../../Components/Icons/Power/controller/controller_level4_charging.svg?react';
import ControllerChargingLevel4IconDark from '../../Components/Icons/Power/controller/controller_level4_charging_dark.svg?react';
import ControllerChargingLevel5Icon from '../../Components/Icons/Power/controller/controller_level5_charging.svg?react';
import ControllerChargingLevel5IconDark from '../../Components/Icons/Power/controller/controller_level5_charging_dark.svg?react';
import ControllerDisconnectedIcon from '../../Components/Icons/Power/controller/controller_disconnected.svg?react';
import ControllerDisconnectedIconDark from '../../Components/Icons/Power/controller/controller_disconnected_dark.svg?react';
import ControllerLevel1Icon from '../../Components/Icons/Power/controller/controller_level1.svg?react';
import ControllerLevel1IconDark from '../../Components/Icons/Power/controller/controller_level1_dark.svg?react';
import ControllerLevel2Icon from '../../Components/Icons/Power/controller/controller_level2.svg?react';
import ControllerLevel2IconDark from '../../Components/Icons/Power/controller/controller_level2_dark.svg?react';
import ControllerLevel3Icon from '../../Components/Icons/Power/controller/controller_level3.svg?react';
import ControllerLevel3IconDark from '../../Components/Icons/Power/controller/controller_level3_dark.svg?react';
import ControllerLevel4Icon from '../../Components/Icons/Power/controller/controller_level4.svg?react';
import ControllerLevel4IconDark from '../../Components/Icons/Power/controller/controller_level4_dark.svg?react';
import ControllerLevel5Icon from '../../Components/Icons/Power/controller/controller_level5.svg?react';
import ControllerLevel5IconDark from '../../Components/Icons/Power/controller/controller_level5_dark.svg?react';
import { RootState } from '../../redux/Store';
import { BATTERY_STATES } from '../../utils/constants';

const CONTROLLER_POWER_ICONS = {
  charging_level1: { light: ControllerChargingLevel1Icon, dark: ControllerChargingLevel1IconDark },
  charging_level2: { light: ControllerChargingLevel2Icon, dark: ControllerChargingLevel2IconDark },
  charging_level3: { light: ControllerChargingLevel3Icon, dark: ControllerChargingLevel3IconDark },
  charging_level4: { light: ControllerChargingLevel4Icon, dark: ControllerChargingLevel4IconDark },
  charging_level5: { light: ControllerChargingLevel5Icon, dark: ControllerChargingLevel5IconDark },
  level1: { light: ControllerLevel1Icon, dark: ControllerLevel1IconDark },
  level2: { light: ControllerLevel2Icon, dark: ControllerLevel2IconDark },
  level3: { light: ControllerLevel3Icon, dark: ControllerLevel3IconDark },
  level4: { light: ControllerLevel4Icon, dark: ControllerLevel4IconDark },
  level5: { light: ControllerLevel5Icon, dark: ControllerLevel5IconDark },
} as const;

export type ControllerPowerIconColorMode = 'light' | 'dark';

const CONTROLLER_DISCONNECTED_ICONS = {
  light: ControllerDisconnectedIcon,
  dark: ControllerDisconnectedIconDark,
} as const;

const renderControllerIcon = (Icon: ComponentType<SVGProps<SVGSVGElement>>) => <Icon aria-hidden focusable="false" width={24} height={24} />;

const useControllerPowerStateIcon = (colorMode: ControllerPowerIconColorMode): JSX.Element | null => {
  const { controllerConnected } = useSelector((state: RootState) => state.application);
  const { batteryCharging, batteryLevel } = useSelector((state: RootState) => state.controllerData);

  const isDark = colorMode === 'dark';

  if (!controllerConnected) {
    const Icon = isDark ? CONTROLLER_DISCONNECTED_ICONS.dark : CONTROLLER_DISCONNECTED_ICONS.light;
    return renderControllerIcon(Icon);
  }

  const pick = (key: keyof typeof CONTROLLER_POWER_ICONS) => (isDark ? CONTROLLER_POWER_ICONS[key].dark : CONTROLLER_POWER_ICONS[key].light);

  let Icon: ComponentType<SVGProps<SVGSVGElement>>;
  switch (batteryLevel) {
    case BATTERY_STATES.LEVEL5:
      Icon = pick(batteryCharging ? 'charging_level5' : 'level5');
      break;
    case BATTERY_STATES.LEVEL4:
      Icon = pick(batteryCharging ? 'charging_level4' : 'level4');
      break;
    case BATTERY_STATES.LEVEL3:
      Icon = pick(batteryCharging ? 'charging_level3' : 'level3');
      break;
    case BATTERY_STATES.LEVEL2:
      Icon = pick(batteryCharging ? 'charging_level2' : 'level2');
      break;
    case BATTERY_STATES.LEVEL1:
      Icon = pick(batteryCharging ? 'charging_level1' : 'level1');
      break;
    default:
      // batteryLevel is null before the first system-state poll; use level3 as a
      // conservative mid-range fallback rather than an optimistic full-charge icon.
      Icon = pick(batteryCharging ? 'charging_level3' : 'level3');
  }

  return renderControllerIcon(Icon);
};

export default useControllerPowerStateIcon;
